<template>
  <div class="cart">
    <overlay :open="open" @close="close" />
    <transition name="cart-drawer" appear>
      <div class="cart-drawer" v-if="open">
        <div class="top">
          <h3 class="title">{{ $translations.cart.general.title }}</h3>
          <button class="close" @click="close">
            {{ $translations.cart.general.close }}
          </button>
        </div>
        <div
          class="free-shipping"
          v-if="false && amountLeft < freeShippingTarget"
        >
          {{ amountLeft }} - {{ progressString }}
        </div>
        <template v-if="data.items.length > 0">
          <ul class="item-list">
            <li v-for="(item, index) in data.items" :key="index" class="item">
              <cart-item
                v-bind="item"
                :lineKey="item.key"
                :recharge="item.properties && item.properties.subscription_id"
              />
            </li>
          </ul>
          <div class="total">
            <div class="subtotal">
              <span>{{ $translations.cart.general.subtotal }}</span>

              <div class="subtotal__prices">
                <span v-if="comparePrice" class="compare-price">{{ $formatMoney(data.original_total_price) }}</span>
                <span class="price">{{ $formatMoney(data.total_price) }}</span>
              </div>
            </div>

            <div class="terms">
              <input
                type="checkbox"
                v-model="termsAccepted"
                id="cart-terms"
                class="terms__checkbox"
              />
              <label
                for="cart-terms"
                class="terms__label t-linkify"
                v-html="termsAndConditions"
              />
            </div>

            <span
              id="clerk-sidebar"
              class="clerk"
              data-template="@power-step-others-also-bought"
              :data-products="clerkDataProducts"
            ></span>

            <a
              href="/checkout"
              class="button checkout-button"
              :disabled="!termsAccepted"
              @click="handleCheckoutClick($event)"
            >
              {{ $translations.cart.general.checkout }}
            </a>
          </div>
        </template>
        <div class="empty" v-else>
          <div class="empty__pictogram">
            <i class="fal fa-dolly-empty"></i>
          </div>
          <p class="empty__lead">{{ $translations.cart.general.empty }}</p>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from "vuex";
import Overlay from "../overlay.vue";
import CartItem from "./cart-item.vue";
import store from "../../store";
import { removeURLParameter } from "../../util";

export default {
  name: "CartDrawer",
  components: { CartItem, Overlay },
  store,
  selector: "#vue-cart-drawer",
  data() {
    return {
      termsAccepted: false,
    };
  },
  props: {
    freeShippingTarget: {
      type: Number,
      required: true,
    },
    strings: {
      type: Object,
      required: true,
    },
  },
  computed: {
    ...mapGetters({
      data: "cart/data",
      open: "cart/open",
    }),

    comparePrice() {
      return this.data.total_price < this.data.original_total_price;
    },

    clerkDataProducts() {
      return (
        "[" + this.data.items.map((item) => item.product_id).join(",") + "]"
      );
    },

    amountLeft() {
      return this.freeShippingTarget - this.data.total_price / 100;
    },
    progressString() {
      return this.strings.free_shipping_progress_text
        .replace(/{{\s*target\s*}}/g, this.freeShippingTarget)
        .replace(/{{\s*amount_left\s*}}/g, this.amountLeft);
    },

    termsAndConditions() {
      // this might be a little stupid...
      return this.strings.terms_and_conditions
        .replace(/<p[^>]*>/, "<span>")
        .replace(/<\/p>/, "</span>");
    },
  },
  methods: {
    ...mapActions({
      changeItem: "cart/change",
      addToCart: "cart/add",
      getData: "cart/getData"
    }),
    ...mapMutations({
      setCartOpen: "cart/open",
    }),
    close() {
      this.setCartOpen(false);
    },
    processCart() {
      // const url = window.reChargeProcessCart();
      // window.location = url;
    },
    handleCheckoutClick(evt) {
      if (!this.termsAccepted) {
        evt.stopPropagation();
        evt.preventDefault();
      }
    },
    initializeClerk() {
      if (window.Clerk) {
        Clerk("content", "#clerk-sidebar");
      }
    },
  },
  created() {
    if (window.location.search.includes("cart=1")) {
      this.setCartOpen(true);
      const url = window.location.toString();
      const newUrl = removeURLParameter(url, "cart");
      window.history.replaceState(null, "", newUrl);
    }

    document.addEventListener('cart:open', () => {
      this.setCartOpen(true);
    })

    const triggers = document.querySelectorAll('[data-cart-trigger]');
    triggers.forEach(trigger => {
      trigger.addEventListener('click', () => {
        this.setCartOpen(true);
      })
    })

    document.addEventListener('octane.quiz.addToCart',  (e) => {
      e.detail.items.forEach(item => {
        const data = {
          id: item.id,
          quantity: item.quantity
        }
        this.addToCart(data);
      })
    });

    document.addEventListener('cart:item:added', () => {
      this.getData();
    });
  },
  watch: {
    data(cart) {
      const itemCounts = document.querySelectorAll('[data-cart-count]');
      itemCounts.forEach(item => {
        item.hidden = cart.item_count === 0
        item.textContent = cart.item_count.toString();
      });
    },
    open(open) {
      this.$nextTick(() => {
        this.initializeClerk();
      });
    },
    clerkDataProducts() {
      this.initializeClerk();
    },
  },
};
</script>

<style lang="scss" scoped>
.cart ::v-deep {
  .overlay {
    z-index: z-index(cart-drawer) - 1 !important;
  }
}

.cart-drawer {
  position: fixed;
  top: 0;
  right: 0;
  max-width: 480px;
  width: 100%;
  padding: 56px;
  height: 100vh;
  overflow-y: scroll;
  z-index: z-index(cart-drawer);
  background-color: white;

  &-enter-active,
  &-leave-active {
    transition: transform 0.2s ease-in-out;
  }

  &-enter,
  &-leave-to {
    transform: translateX(100%);
  }
}

.top {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.title {
  margin: 0;
}

.price,
.compare-price {
  margin-top: 8px;
  margin-bottom: 0;
  font-family: $font-family-mono;
}

.compare-price {
  opacity: 0.333;
  text-decoration: line-through;
}

.close {
  @include button-reset();
  margin-left: 48px;
  font-family: $font-family-mono;
  font-size: 14px;
  color: #334242;
  letter-spacing: 1px;
  text-transform: uppercase;
}

.item-list {
  padding: 0;
  margin: 32px 0 0 0;
  list-style: none;

  * + * {
    margin-top: 8px;
  }
}

.terms {
  margin-top: 40px;
  display: flex;
  font-size: 14px;
  padding: 8px 4px;
  background-color: #edf2f1;
  border: 1px solid #dce7e4;
  border-radius: 4px;

  &__checkbox {
    appearance: none;
    background-color: $white;
    border: 1px solid #e2e8e6;
    width: 20px;
    height: 20px;
    display: inline-block;
    position: relative;
    flex-shrink: 0;

    &:active,
    &:checked:active {
    }

    &:checked {
      &::after {
        content: "\2714";
        font-size: 14px;
        position: absolute;
        top: 0px;
        left: 3px;
        color: $black;
      }
    }
  }

  &__label {
    flex: 1;
    margin-top: 5px;
    margin-left: 8px;

    /* ::v-deep {
			p {
				margin: 0;
				font-size: 14px !important;
				font-weight: 400 !important;
			}
		} */
  }
}

.total {
  margin-top: 40px;
  border-top: 1px solid black;
  padding-top: 16px;
}

.subtotal {
  display: flex;
  justify-content: space-between;
}

.subtotal__prices {

}

.checkout-button {
  margin-top: 16px;
  width: 100%;
}

.empty {
  text-align: center;
  &__pictogram {
    font-size: 80px;
    padding: 40px 0;
  }
}
</style>
